import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { Button, Col, Form, Row, Select, DatePicker, message, Spin, Result, Modal, Input, Alert, } from "antd";
import { connect } from "dva";
import moment from "moment";
import { router } from "umi";
import { LocationType } from "@/typedefs/address";
import { SecurePassAutoSend } from "@/typedefs/securePass";
import { ShipmentStatus } from "@/typedefs/shipment";
import { useStates } from "@/utils/address";
import { parseUtcToLocal } from "@/utils/dateFormatter";
import { Integrations, TrailerType } from "@/utils/enum";
import { disableDatesBeforeToday, checkUniqueAddresses, getUrlParams, DEFAULT_ID } from "@/utils/utils";
import { Validator } from "@/utils/validator";
import { getDealersAllowed } from "@/services/dealer";
import { fetchAssociatedUsers } from "@/services/entityGroups";
import { createDraftShipment, getSingleAdminShipment, getSingleShipment } from "@/services/shipments";
import { editAdminShipment, editShipment } from "@/services/shipments_new";
import { getAdminManagers, getAdminUserById } from "@/services/users_admin";
import useScreenDetector from "@/hooks/useScreenDetector";
import DealerInput from "@/components/DealerInput";
import NotFoundContent from "@/components/NotFoundContent";
import { flatPhoneNumber } from "@/components/PhoneNumberInput";
import SelectAutoComplete from "@/components/SelectAutocomplete";
import IntegrationInputs from "./components/IntegrationInputs";
import LocationInformationInputs from "./components/LocationInformationInputs";
import SpecialConditionsInputs from "./components/SpecialConditionsInputs";
import initialValues from "./constants/initialValues";
import { parseAddressData } from "./utils";
const { useForm } = Form;
const { Option } = Select;
const { confirm } = Modal;
const { TextArea } = Input;
const breakPoints = { xs: 24, sm: 24, md: 24, lg: 12, xl: 12, xxl: 12 };
const editingRotes = ["/shipments/dealer/edit", "/admin/shipments/edit"];
const disabledStatuses = [ShipmentStatus.Delivered, ShipmentStatus.Incomplete];
const ShipmentForm = ({ configPerm: paramConfigPerm }) => {
    const configPerm = useMemo(() => paramConfigPerm || {}, [paramConfigPerm]);
    const { isMobile } = useScreenDetector();
    const lastShipmentFetched = useRef(null);
    const createShipmentFormRef = useRef(null);
    const wwlIntegrationFormRef = useRef(null);
    const [restrictedFieldsFormRef] = useForm();
    // store a copy of the original addresses from the address book to check later if changes were made
    const originalSelectedAddresses = useRef({});
    const isAdmin = useMemo(() => configPerm["CoreService.AdminShipments"], [configPerm]);
    const isEditing = useMemo(() => editingRotes.includes(window.location.pathname), []);
    const [submitting, setSubmitting] = useState(false);
    const [loadingShipmentInfo, setLoadingShipmentInfo] = useState(isEditing ?? false);
    const [originalShipmentData, setOriginalShipmentData] = useState(null);
    const [dealersHookDisabled, setDealersHookDisabled] = useState(isEditing ?? false);
    const [isSpecialConditionsOpen, setIsSpecialConditionsOpen] = useState(false);
    const [wwlIntegrationInputsVisible, setWWLIntegrationInputsVisible] = useState(false);
    const [showSelectAvailableDate, setShowSelectAvailableDate] = useState(isEditing);
    const [dealerInputHidden, setDealerInputHidden] = useState(true);
    const [connectedUsers, setConnectedUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [dealerId, setDealerId] = useState(null);
    const [securePassInputVisible, setSecurePassInputVisible] = useState(!isEditing);
    const [restrictedFieldsChanged, setRestrictedFieldsChanged] = useState(null);
    const [pickupRestrictedDays, setPickupRestrictedDays] = useState([]);
    const [dropoffRestrictedDays, setDropoffRestrictedDays] = useState([]);
    const hasErrors = useMemo(() => {
        const allDaysSelectedAsPickup = pickupRestrictedDays.length === 7;
        const allDaysSelectedAsDropoff = dropoffRestrictedDays.length === 7;
        return allDaysSelectedAsPickup || allDaysSelectedAsDropoff;
    }, [pickupRestrictedDays, dropoffRestrictedDays]);
    const [selectedManagerId, setSelectedManagerId] = useState(null);
    const [managerSingleOption, setManagerSingleOption] = useState(false);
    const { states: allStates, loading: loadingStates } = useStates();
    const shipmentId = useMemo(() => getUrlParams("shipmentId"), []);
    const formDisabled = useMemo(() => isEditing && (!originalShipmentData || disabledStatuses.includes(originalShipmentData.status)), [isEditing, originalShipmentData]);
    const trailerTypeRestricted = useMemo(() => Boolean(isEditing && originalShipmentData && originalShipmentData.status >= ShipmentStatus.Posted), [isEditing, originalShipmentData]);
    const availableAtRestricted = useMemo(() => Boolean(isEditing && originalShipmentData && originalShipmentData.status >= ShipmentStatus.Posted), [isEditing, originalShipmentData]);
    const specialConditionsRestricted = useMemo(() => Boolean(isEditing && originalShipmentData && originalShipmentData?.status >= ShipmentStatus.Posted), [isEditing, originalShipmentData]);
    const handleIsSpecialConditionsOpenChange = useCallback(([isOpen]) => {
        setIsSpecialConditionsOpen(isOpen);
    }, []);
    const onAvailableChange = useCallback(value => {
        setShowSelectAvailableDate(!value);
    }, []);
    const updateShipperSelectOptions = useCallback(async (newDealerId) => {
        if (!configPerm["CoreService.AssociateUsers"]) {
            return;
        }
        setLoadingUsers(true);
        try {
            const res = await fetchAssociatedUsers(newDealerId);
            const items = (res?.items || []);
            setConnectedUsers(items);
            if (isAdmin && items.length === 1) {
                createShipmentFormRef.current?.setFieldsValue({
                    userId: items[0].userId,
                });
            }
        }
        catch (err) {
            console.error(err);
        }
        setLoadingUsers(false);
    }, [configPerm, isAdmin]);
    const getPayload = useCallback(formValues => {
        // validate addresses are unique
        const validAddresses = checkUniqueAddresses(formValues.Pickup, formValues.Dropoff);
        if (!validAddresses) {
            message.error("Pickup and Dropoff addresses cannot be the same");
            return null;
        }
        const pickupDayOfWeekRestricted = formValues.Pickup.dayOfWeekRestricted;
        const dropoffDayOfWeekRestricted = formValues.Dropoff.dayOfWeekRestricted;
        const values = {
            ...formValues,
            Pickup: {
                ...formValues.Pickup,
                contactPhone: flatPhoneNumber(formValues.Pickup.contactPhone),
                dayOfWeekRestricted: typeof pickupDayOfWeekRestricted === "string"
                    ? pickupDayOfWeekRestricted
                    : (pickupDayOfWeekRestricted || []).sort().toString(),
                contactEmail: formValues.Pickup.contactEmails?.join(","),
                latitude: formValues.Pickup.latitude,
                longitude: formValues.Pickup.longitude,
            },
            Dropoff: {
                ...formValues.Dropoff,
                contactPhone: flatPhoneNumber(formValues.Dropoff.contactPhone),
                dayOfWeekRestricted: typeof dropoffDayOfWeekRestricted === "string"
                    ? dropoffDayOfWeekRestricted
                    : (dropoffDayOfWeekRestricted || []).sort().toString(),
                contactEmail: formValues.Dropoff.contactEmails?.join(","),
                latitude: formValues.Dropoff.latitude,
                longitude: formValues.Dropoff.longitude,
            },
        };
        const createShipmentRequestData = {};
        if (isAdmin && isEditing) {
            createShipmentRequestData.autosledManagerId = formValues.autosledManagerId ?? null;
        }
        // Availability
        createShipmentRequestData.isAvailable = values.isAvailable;
        if (!values.isAvailable) {
            // TODO: We need to handle this as a local date time to ensure the date part matches with the selected date
            createShipmentRequestData.availableAt = values.availableAt.toISOString();
        }
        // Trailer Type
        createShipmentRequestData.trailerType = values.trailerType;
        // check if the pickup address is the same as the original one
        const isSamePickupAsOriginal = !checkUniqueAddresses(values.Pickup, originalSelectedAddresses.current.Pickup);
        // If autofilled from address book and the pickup address is the same as the original one, include that addressId in request
        if (values.Pickup.originalAddressId && isSamePickupAsOriginal) {
            createShipmentRequestData.originalPickupAddressId = values.Pickup.originalAddressId;
        }
        createShipmentRequestData.pickupAddress = parseAddressData(values.Pickup);
        createShipmentRequestData.pickupAddress.id = originalShipmentData?.pickupAddress.id;
        createShipmentRequestData.pickupAddress.dealerId = values.dealerId;
        createShipmentRequestData.pickupAddress.notes = values.Pickup.instructionsOrRestrictions;
        if (!securePassInputVisible) {
            createShipmentRequestData.pickupAddress.autosendType = SecurePassAutoSend.DoNotSend;
        }
        // check if the pickup address is the same as the original one
        const isSameDropoffAsOriginal = !checkUniqueAddresses(values.Dropoff, originalSelectedAddresses.current.Dropoff);
        // If autofilled from address book & the pickup address is the same as the original one, include that addressId in request
        if (values.Dropoff.originalAddressId && isSameDropoffAsOriginal) {
            createShipmentRequestData.originalDropOffAddressId = values.Dropoff.originalAddressId;
        }
        createShipmentRequestData.dropOffAddress = parseAddressData(values.Dropoff);
        createShipmentRequestData.dropOffAddress.id = originalShipmentData?.dropOffAddress.id;
        createShipmentRequestData.dropOffAddress.dealerId = values.dealerId;
        createShipmentRequestData.dropOffAddress.notes = values.Dropoff.instructionsOrRestrictions;
        createShipmentRequestData.dealerId = values.dealerId;
        if (values.userId) {
            createShipmentRequestData.contactShipperId = values.userId;
        }
        // Special conditions
        createShipmentRequestData.requiresTWIC = values.requiresTWIC ?? false;
        createShipmentRequestData.requiresEModal = values.requiresEModal ?? false;
        createShipmentRequestData.requiresLotExit = values.requiresLotExit ?? false;
        // Optional integration inputs
        if (wwlIntegrationInputsVisible) {
            const { vpc, railHead, carrierCode, sequenceNumber } = wwlIntegrationFormRef?.current?.getFieldsValue() ?? {};
            createShipmentRequestData.vpc = vpc;
            createShipmentRequestData.railHead = railHead;
            createShipmentRequestData.carrierCode = carrierCode;
            createShipmentRequestData.sequenceNumber = sequenceNumber;
            createShipmentRequestData.tenderFileUrl = "test";
            createShipmentRequestData.integrationType = Integrations.WWL;
        }
        return createShipmentRequestData;
    }, [isEditing, isAdmin, originalShipmentData, securePassInputVisible, wwlIntegrationInputsVisible]);
    const onSelectDealer = useCallback(({ id, integrations }) => {
        createShipmentFormRef.current?.setFieldsValue({
            userId: null,
        });
        setWWLIntegrationInputsVisible(integrations?.some((integration) => integration.type === Integrations.WWL));
        setDealerId(id);
    }, []);
    const handleCreateShipment = useCallback(async (payload) => {
        setSubmitting(true);
        try {
            const response = await createDraftShipment(payload);
            if ("error" in response) {
                return;
            }
            message.success("Successfully created shipment!");
            const { id: newShipmentId } = response;
            const url = isAdmin
                ? `/admin/shipments/view?shipmentId=${newShipmentId}`
                : `/shipments/dealer/all/view?shipmentId=${newShipmentId}`;
            router.push(url);
        }
        catch (err) {
            console.error(err);
        }
        finally {
            setSubmitting(false);
        }
    }, [isAdmin]);
    const handleEditShipment = useCallback(async (payload) => {
        const endpoint = isAdmin ? editAdminShipment : editShipment;
        try {
            const response = await endpoint(shipmentId, payload);
            if ("error" in response) {
                return;
            }
            message.success("Successfully edited shipment!");
            const { id: shipmentIdResponse } = response; // should remain the same, but just in case...
            const url = isAdmin
                ? `/admin/shipments/view?shipmentId=${shipmentIdResponse}`
                : `/shipments/dealer/all/view?shipmentId=${shipmentIdResponse}`;
            router.push(url);
        }
        catch (err) {
            console.error(err);
        }
        finally {
            setSubmitting(false);
        }
    }, [isAdmin, shipmentId]);
    const handleSubmitPayload = useCallback(async (payload) => {
        if (isEditing) {
            handleEditShipment(payload);
        }
        else {
            handleCreateShipment(payload);
        }
    }, [isEditing, handleCreateShipment, handleEditShipment]);
    const checkForRestrictedFieldsChanges = useCallback((payload) => {
        if (!isEditing || originalShipmentData === null || !configPerm["CoreService.AdminShipments.Edit"]) {
            // if creating a shipment or it is a dealer do not check for restricted fields
            // creating a shipment doesn't have any restricted field
            // dealers can't edit those fields
            return false;
        }
        if ([ShipmentStatus.Draft, ShipmentStatus.Pending, ShipmentStatus.Completed].includes(originalShipmentData.status)) {
            // this shipments is safe to edit, restricted fields warning is only needed for posted and assigned shipments
            return false;
        }
        const trailerTypeChanged = trailerTypeRestricted && payload.trailerType !== originalShipmentData.trailerType;
        const availableAtChanged = availableAtRestricted && !moment(payload.availableAt).isSame(originalShipmentData.availableAt, "day");
        const specialConditionsChanged = (specialConditionsRestricted && payload.requiresTWIC !== originalShipmentData.requiresTWIC) ||
            payload.requiresEModal !== originalShipmentData.requiresEModal ||
            payload.requiresLotExit !== originalShipmentData.requiresLotExit;
        const contactShipperChanged = payload.contactShipperId !== originalShipmentData.contactShipperId;
        const mainLevelRestrictedFieldsChanged = trailerTypeChanged || availableAtChanged || specialConditionsChanged || contactShipperChanged;
        if (mainLevelRestrictedFieldsChanged) {
            return true;
        }
        const pickupAddressChanged = payload.pickupAddress?.addressLine1 !== originalShipmentData.pickupAddress.addressLine1 ||
            payload.pickupAddress?.addressLine2 !== originalShipmentData.pickupAddress.addressLine2 ||
            payload.pickupAddress?.city !== originalShipmentData.pickupAddress.city ||
            payload.pickupAddress?.state?.id !== originalShipmentData.pickupAddress.stateId ||
            payload.pickupAddress?.zipCode !== originalShipmentData.pickupAddress.zipCode ||
            payload.pickupAddress?.notes !== originalShipmentData.pickupAddress.notes;
        const dropoffAddressChanged = payload.dropOffAddress?.addressLine1 !== originalShipmentData.dropOffAddress.addressLine1 ||
            payload.dropOffAddress?.addressLine2 !== originalShipmentData.dropOffAddress.addressLine2 ||
            payload.dropOffAddress?.city !== originalShipmentData.dropOffAddress.city ||
            payload.dropOffAddress?.state?.id !== originalShipmentData.dropOffAddress.stateId ||
            payload.dropOffAddress?.zipCode !== originalShipmentData.dropOffAddress.zipCode ||
            payload.dropOffAddress?.notes !== originalShipmentData.dropOffAddress.notes;
        return pickupAddressChanged || dropoffAddressChanged;
    }, [
        availableAtRestricted,
        configPerm,
        isEditing,
        originalShipmentData,
        specialConditionsRestricted,
        trailerTypeRestricted,
    ]);
    const checkPickupContactEmail = useCallback((payload) => {
        const { pickupAddress } = payload;
        const hasContactEmail = pickupAddress?.contactEmail !== undefined &&
            pickupAddress?.contactEmail !== null &&
            pickupAddress?.contactEmail !== "";
        const autosendTypesWithContactEmail = [
            SecurePassAutoSend.Default,
            SecurePassAutoSend.Send,
        ];
        return autosendTypesWithContactEmail.includes(pickupAddress?.autosendType) && !hasContactEmail;
    }, []);
    const submitWithValidations = useCallback(async ({ payload, validateRestrictedFields = true, validatePickupContactEmail = true, }) => {
        const restrictedFieldsDidChange = validateRestrictedFields && checkForRestrictedFieldsChanges(payload);
        if (restrictedFieldsDidChange) {
            setRestrictedFieldsChanged(payload);
            return;
        }
        const contactEmailMissingWithAutosend = validatePickupContactEmail && checkPickupContactEmail(payload);
        if (contactEmailMissingWithAutosend) {
            confirm({
                title: "Warning: Pickup Contact Email Not Set",
                okText: "Yes",
                content: (React.createElement("p", null,
                    "This shipment is set to enable the Autosled ",
                    React.createElement("b", null, "SecurePass\u2122"),
                    " process. This process requires a pickup contact email to be set, but no email was provided.",
                    React.createElement("br", null),
                    React.createElement("br", null),
                    "Are you sure you want to continue?")),
                onOk: () => {
                    submitWithValidations({ payload, validateRestrictedFields, validatePickupContactEmail: false });
                },
                onCancel: () => {
                    setSubmitting(false);
                },
            });
            return;
        }
        handleSubmitPayload(payload);
    }, [checkForRestrictedFieldsChanges, checkPickupContactEmail, handleSubmitPayload]);
    const onSubmit = useCallback(async (formValues) => {
        setSubmitting(true);
        const payload = getPayload(formValues);
        if (payload === null) {
            // Some validation failed, the error message is already displayed by this point
            setSubmitting(false);
            return;
        }
        submitWithValidations({ payload });
    }, [getPayload, submitWithValidations]);
    const copyOriginalAddress = useCallback((address, type) => {
        originalSelectedAddresses.current[type] = address;
    }, []);
    const onDealersFetched = useCallback((dealers, searchText) => {
        // if there is only one dealer, get the shippers for that dealer
        if (dealers.length === 1 && searchText.length === 0) {
            setDealerId(dealers[0].id);
        }
        // We need to wait until /core-service/dealers/allowed endpoint is called
        // by the DealerInput component because the response of that endpoint has
        // the integration info for each dealer, while the get shipment by id
        // endpoints dealer data hasn't
        if (!dealerId) {
            // So if there isn't a previously selected dealer let's skip
            return;
        }
        const dealer = dealers.find((dealerItem) => dealerItem.id === dealerId);
        const integrations = dealer?.integrations ?? [];
        setWWLIntegrationInputsVisible(integrations.some(integration => integration.type === Integrations.WWL));
    }, [dealerId]);
    const fetchDealerInfo = useCallback((dealerIdToFetch) => {
        getDealersAllowed({ filter: dealerIdToFetch })
            .then((response) => {
            const dealer = response.items[0];
            setSecurePassInputVisible(dealer.autosendType !== SecurePassAutoSend.DoNotSend);
        })
            .catch(error => console.error(error));
    }, []);
    const fetchManagers = useCallback((searchText) => getAdminManagers({}).then(async (response) => {
        const items = response || [];
        if (selectedManagerId !== null && selectedManagerId !== DEFAULT_ID) {
            const alreadyFetched = items.some(adm => adm.id === selectedManagerId);
            if (!alreadyFetched) {
                const currentManager = await getAdminUserById(selectedManagerId);
                items.push(currentManager);
            }
        }
        setManagerSingleOption(searchText.length === 0 && items.length === 1);
        // this endpoint do not have filters :P
        return items.filter(item => item.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
            item.email.toLowerCase().includes(searchText.toLowerCase()));
    }), [selectedManagerId]);
    useEffect(() => {
        if (!dealerId) {
            return;
        }
        fetchDealerInfo(dealerId);
        if (isAdmin) {
            updateShipperSelectOptions(dealerId);
        }
    }, [dealerId, isAdmin, updateShipperSelectOptions, fetchDealerInfo]);
    useEffect(() => {
        if (!isEditing) {
            setLoadingShipmentInfo(false);
            return;
        }
        if (allStates.length === 0 || lastShipmentFetched.current === shipmentId) {
            return;
        }
        lastShipmentFetched.current = shipmentId;
        setLoadingShipmentInfo(true);
        const endpoint = isAdmin ? getSingleAdminShipment : getSingleShipment;
        endpoint(shipmentId)
            .then(async (res) => {
            if (!res) {
                return;
            }
            setOriginalShipmentData(res);
            setDealersHookDisabled(false);
            if (isAdmin) {
                setDealerId(res.dealer.id);
                onSelectDealer(res.dealer);
            }
            const pickupState = allStates.find(state => state.id === res.pickupAddress.stateId);
            const dropoffState = allStates.find(state => state.id === res.dropOffAddress.stateId);
            setIsSpecialConditionsOpen(res.requiresTWIC || res.requiresEModal || res.requiresLotExit);
            createShipmentFormRef.current?.setFieldsValue({
                ...res,
                userId: res?.contactShipperId,
                autosledManagerId: res?.autosledManagerId,
                availableAt: res.availableAt ? parseUtcToLocal(res.availableAt) : moment(),
                originalPickUpAddressId: res.pickupAddress.id,
                originalDropOffAddressId: res.dropOffAddress.id,
                Pickup: {
                    ...res.pickupAddress,
                    addressId: res.pickupAddress.id,
                    stateName: pickupState?.name,
                    stateShortName: pickupState?.shortName,
                    locationName: res.pickupAddress.name,
                    instructionsOrRestrictions: res.pickupAddress.notes,
                    originalAddressId: res.originalPickupAddressId,
                    autosendType: res.pickupAddress.autosendType === 0 ? SecurePassAutoSend.Default : res.pickupAddress.autosendType,
                    dayOfWeekRestricted: res.pickupAddress?.dayOfWeekRestricted?.split(","),
                    ...(isEditing && {
                        contactEmails: res.pickupAddress?.contactEmail?.split(",")?.filter(Boolean),
                    }),
                },
                Dropoff: {
                    ...res.dropOffAddress,
                    addressId: res.dropOffAddress.id,
                    stateName: dropoffState?.name,
                    stateShortName: dropoffState?.shortName,
                    locationName: res.dropOffAddress.name,
                    instructionsOrRestrictions: res.dropOffAddress.notes,
                    originalAddressId: res.originalDropOffAddressId,
                    dayOfWeekRestricted: res.dropOffAddress?.dayOfWeekRestricted?.split(","),
                },
            });
        })
            .finally(() => {
            setLoadingShipmentInfo(false);
        });
    }, [isAdmin, shipmentId, allStates, loadingStates, isEditing, onSelectDealer]);
    useEffect(() => {
        if (wwlIntegrationInputsVisible) {
            wwlIntegrationFormRef.current?.setFieldsValue({
                vpc: originalShipmentData?.vpc,
                railHead: originalShipmentData?.railHead,
                carrierCode: originalShipmentData?.carrierCode,
                sequenceNumber: originalShipmentData?.sequenceNumber,
            });
        }
    }, [wwlIntegrationInputsVisible, originalShipmentData]);
    const trailerTypeColumns = useMemo(() => {
        const columnsWidth = {
            xs: 24,
            md: 24,
            xl: dealerInputHidden ? 12 : 8,
        };
        return columnsWidth;
    }, [dealerInputHidden]);
    const isAvailableColumns = useMemo(() => {
        const columnsWidth = {
            xs: 24,
            md: showSelectAvailableDate ? 12 : 24,
            xl: 24,
        };
        if (dealerInputHidden) {
            columnsWidth.xl = showSelectAvailableDate ? 6 : 12;
        }
        else {
            columnsWidth.xl = showSelectAvailableDate ? 4 : 8;
        }
        return columnsWidth;
    }, [dealerInputHidden, showSelectAvailableDate]);
    const availableAtColumns = useMemo(() => {
        const columnsWidth = {
            xs: 24,
            md: isEditing ? 24 : 12,
            xl: 24,
        };
        if (isEditing) {
            columnsWidth.xl = dealerInputHidden ? 12 : 8;
        }
        else {
            columnsWidth.xl = dealerInputHidden ? 6 : 4;
        }
        return columnsWidth;
    }, [dealerInputHidden, isEditing]);
    if (!loadingShipmentInfo && isEditing && !originalShipmentData) {
        return (React.createElement(Result, { status: "404", title: "Shipment Not Found", subTitle: "The shipment you are trying to edit does not exist or you may not have permission to view it.", extra: React.createElement(Button, { type: "primary", onClick: () => {
                    if (isAdmin) {
                        router.push("/admin/shipments");
                    }
                    else {
                        router.push("/shipments/dealer/all");
                    }
                } }, "Back to Shipments") }));
    }
    return (React.createElement(Spin, { spinning: loadingShipmentInfo },
        React.createElement(Form, { ref: createShipmentFormRef, layout: "vertical", initialValues: {
                isAvailable: true,
                trailerType: TrailerType.Open,
                Pickup: {
                    ...initialValues,
                    originalPickupAddressId: originalShipmentData?.originalPickupAddressId,
                },
                DropOff: {
                    ...initialValues,
                    originalDropOffAddressId: originalShipmentData?.originalDropOffAddressId,
                },
                // Special Conditions
                requiresTWIC: false,
                requiresEModal: false,
                requiresLotExit: false,
            }, style: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
            }, onFinish: onSubmit, scrollToFirstError: { behavior: "smooth" } },
            React.createElement(Row, { gutter: 16, style: { width: "100%" } },
                React.createElement(Col, { flex: 1 },
                    React.createElement(Row, { gutter: 16 },
                        React.createElement(DealerInput, { formRef: createShipmentFormRef, disabled: formDisabled, hookDisabled: dealersHookDisabled, dealerId: dealerId, rules: [Validator.required("Please select a dealer")], onChange: onSelectDealer, onHiddenChange: setDealerInputHidden, onDealersFetched: onDealersFetched, xs: 24, sm: 24, md: 24, lg: 24, xl: 8, xxl: 8 }),
                        React.createElement(Col, { xs: trailerTypeColumns.xs, md: trailerTypeColumns.md, xl: trailerTypeColumns.xl },
                            React.createElement(Form.Item, { name: "trailerType", label: "Trailer Type", rules: [Validator.required("Please select a trailer type")], style: { width: "100%" } },
                                React.createElement(Select, { disabled: formDisabled || (!configPerm["CoreService.AdminShipments.Edit"] && trailerTypeRestricted), getPopupContainer: trigger => trigger.parentElement },
                                    React.createElement(Option, { value: TrailerType.Open }, "Open"),
                                    React.createElement(Option, { value: TrailerType.Enclosed }, "Enclosed")))),
                        !isEditing && (React.createElement(Col, { xs: isAvailableColumns.xs, md: isAvailableColumns.md, xl: isAvailableColumns.xl },
                            React.createElement(Form.Item, { name: "isAvailable", label: "Available Now", 
                                // availability not required on form validation for submission to pricing engine
                                rules: [Validator.required("Please select availability")] },
                                React.createElement(Select, { disabled: formDisabled, onChange: onAvailableChange, getPopupContainer: trigger => trigger.parentElement },
                                    React.createElement(Option, { value: true }, "Yes"),
                                    React.createElement(Option, { value: false }, "No"))))),
                        showSelectAvailableDate && (React.createElement(Col, { xs: availableAtColumns.xs, md: availableAtColumns.md, xl: availableAtColumns.xl },
                            React.createElement(Form.Item, { name: "availableAt", label: "Available At", 
                                // availability not required on form validation for submission to pricing engine
                                rules: [Validator.required("Please select a date")] },
                                React.createElement(DatePicker, { format: "MM/DD/YYYY", disabled: formDisabled || (!configPerm["CoreService.AdminShipments.Edit"] && availableAtRestricted), getPopupContainer: trigger => trigger.parentElement ?? trigger, style: { width: "100%" }, disabledDate: disableDatesBeforeToday }))))),
                    !isAdmin && wwlIntegrationInputsVisible && (React.createElement(Row, { gutter: 16, style: { marginBottom: 16 } },
                        React.createElement(Col, { span: 24 },
                            React.createElement(Alert, { type: "warning", message: "WWL Integration", description: "This dealer has a WWL integration enabled. Please contact Autosled Ops for assistance or select a different dealer to continue.", showIcon: true })))),
                    isAdmin && (React.createElement(Row, { gutter: 16 },
                        React.createElement(Col, { xs: 24, md: isEditing ? 12 : 24 },
                            React.createElement(Form.Item, { name: "userId", label: "Shipper", rules: [Validator.required("Please select a user")] },
                                React.createElement(Select, { showSearch: true, placeholder: "Select a user", disabled: formDisabled, notFoundContent: NotFoundContent({
                                        loading: loadingUsers,
                                    }), filterOption: (input, option) => option?.key.toUpperCase().indexOf(input.toUpperCase()) !== -1 }, connectedUsers.map(user => (React.createElement(Select.Option, { key: `${user.userId || ""}${user.userName || ""}${user.email || ""}`, value: user.userId }, user.userName)))))),
                        isEditing && (React.createElement(Col, { xs: 24, md: 12 },
                            React.createElement(Form.Item, { name: "autosledManagerId", label: "Autosled Manager" },
                                React.createElement(SelectAutoComplete, { placeholder: "Select Manager", value: selectedManagerId, disabled: formDisabled, onChange: newManagerId => setSelectedManagerId(newManagerId), autoPopulateOptions: true, fetchOptions: fetchManagers, parseFetchedOptions: async (response) => ({
                                        totalCount: response?.length ?? 0,
                                        items: response ?? [],
                                    }), defaultActiveFirstOption: managerSingleOption, renderOption: manager => (React.createElement(Option, { value: manager.id, key: `${manager.id}${manager.fullName || ""}${manager.email}` },
                                        manager.fullName,
                                        " (",
                                        manager.email,
                                        ")")) })))))),
                    React.createElement(Row, { gutter: 16 },
                        React.createElement(Col, { xs: breakPoints.xs, sm: breakPoints.sm, md: breakPoints.md, lg: breakPoints.lg, xl: breakPoints.xl, xxl: breakPoints.xxl, style: { marginBottom: "20px" } },
                            React.createElement(LocationInformationInputs, { shipmentStatus: originalShipmentData?.status ?? null, canSaveAddress: !isEditing && !!configPerm["CoreService.Addresses.Create"], locationType: LocationType.Pickup, securePassInputVisible: securePassInputVisible, formRef: createShipmentFormRef, dealerId: dealerId, isAdmin: isAdmin, onSelectAddress: (address) => copyOriginalAddress(address, "Pickup"), onRestrictedDaysChange: setPickupRestrictedDays })),
                        React.createElement(Col, { xs: breakPoints.xs, md: breakPoints.md, lg: breakPoints.lg, xl: breakPoints.xl, xxl: breakPoints.xxl, style: { marginBottom: "20px" } },
                            React.createElement(LocationInformationInputs, { shipmentStatus: originalShipmentData?.status ?? null, canSaveAddress: !isEditing && !!configPerm["CoreService.Addresses.Create"], locationType: LocationType.Dropoff, formRef: createShipmentFormRef, dealerId: dealerId, isAdmin: isAdmin, onSelectAddress: (address) => copyOriginalAddress(address, "Dropoff"), onRestrictedDaysChange: setDropoffRestrictedDays }))))),
            isAdmin && wwlIntegrationInputsVisible && (React.createElement("div", { style: { width: "100%" } },
                React.createElement(IntegrationInputs, { wwlIntegrationFormRef: wwlIntegrationFormRef }))),
            isAdmin && (React.createElement("div", { style: { width: "100%" } },
                React.createElement(SpecialConditionsInputs, { disabled: formDisabled || (!configPerm["CoreService.AdminShipments.Edit"] && specialConditionsRestricted), isOpen: isSpecialConditionsOpen, onIsOpenChange: handleIsSpecialConditionsOpenChange }))),
            !formDisabled && (React.createElement("div", { style: {
                    display: "flex",
                    width: "100%",
                    justifyContent: isMobile ? "center" : "flex-end",
                    marginTop: 16,
                } },
                React.createElement(Form.Item, { style: isMobile ? { width: "90%", marginBottom: 0 } : {} },
                    React.createElement(Button, { type: "primary", htmlType: "submit", block: isMobile, loading: submitting, disabled: hasErrors || (!isAdmin && wwlIntegrationInputsVisible) }, isEditing ? "Save" : "Next"))))),
        React.createElement(Modal, { title: "Restricted Fields Changed", open: restrictedFieldsChanged !== null, onOk: restrictedFieldsFormRef.submit, onCancel: () => {
                setRestrictedFieldsChanged(null);
                setSubmitting(false);
            } },
            React.createElement("div", null, "Please provide the reason why you are requesting to continue with the changes."),
            React.createElement(Form, { form: restrictedFieldsFormRef, layout: "vertical", onFinish: formValues => {
                    if (restrictedFieldsChanged === null) {
                        return;
                    }
                    submitWithValidations({
                        payload: {
                            ...restrictedFieldsChanged,
                            reason: formValues.reason,
                        },
                        validateRestrictedFields: false,
                    });
                    setRestrictedFieldsChanged(null);
                } },
                React.createElement(Form.Item, { name: "reason", label: "Reason", rules: [Validator.required("Please give a reason")] },
                    React.createElement(TextArea, { rows: 4 }))))));
};
export default connect(({ configs }) => ({
    configPerm: configs.configPerm,
}))(ShipmentForm);
